import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useStreamers } from '../context/StreamersContext';
import type { StreamerChannelDto, StreamerPublicDto } from '../types';
import '../styles/Streamers.css';

function platformLabel(platform: string): string {
    switch (platform.toLowerCase()) {
        case 'twitch':
            return 'Twitch';
        case 'tiktok':
            return 'TikTok';
        case 'youtube':
            return 'YouTube';
        case 'kick':
            return 'Kick';
        default:
            return 'Live';
    }
}

const LiveCover: React.FC<{ channel: StreamerChannelDto }> = ({ channel }) => {
    let fallback = '';
    if (channel.platform === 'twitch') {
        fallback = `https://static-cdn.jtvnw.net/previews-ttv/live_user_${(channel.handle || '').toLowerCase()}-440x248.jpg`;
    } else if (channel.platform === 'youtube') {
        const vid = (channel.handle || '').trim();
        if (vid.length === 11) fallback = `https://i.ytimg.com/vi/${vid}/hqdefault.jpg`;
    }
    const thumb = channel.thumbnailUrl || fallback;
    if (!thumb) return null;
    return (
        <div className="live-player-wrap">
            <img
                className="live-mini-cover"
                src={thumb}
                alt={channel.handle || 'live'}
                loading="lazy"
            />
            <a
                className="live-player-open"
                href={channel.link}
                target="_blank"
                rel="noopener noreferrer"
                title={channel.link}
            >
                <i className="ri-play-circle-line" />
            </a>
        </div>
    );
};

const Streamers: React.FC = () => {
    const { t, locale } = useLanguage();
    const { streamers, loading } = useStreamers();

    const isLiveUser = (s: StreamerPublicDto) => s.channels.some((c) => c.isLive);
    const live: StreamerPublicDto[] = streamers.filter(isLiveUser);
    const offline: StreamerPublicDto[] = streamers.filter((s) => !isLiveUser(s));

    const renderChannels = (channels: StreamerChannelDto[], primary: boolean) => (
        <div className="live-links">
            {channels
                .filter((c) => (primary ? c.isLive : true))
                .map((c) => (
                    <a
                        key={c.link}
                        className={`btn ${primary ? 'btn-primary' : 'btn-secondary'} btn-block`}
                        href={c.link}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <i className="ri-live-line" /> {t('streamers.watchNow')} · {platformLabel(c.platform)}
                    </a>
                ))}
        </div>
    );

    return (
        <div className="lives-page">
            <div className="container">
                <h1 className="page-title">
                    LIVES <span className="highlight">{t('streamers.titleHighlight')}</span>
                </h1>
                <p className="lives-subtitle">{t('streamers.subtitle')}</p>

                {loading ? (
                    <div className="lives-loading">{t('rankings.loading')}</div>
                ) : live.length > 0 ? (
                    <>
                        <div className="lives-grid">
                            {live.map((streamer) => {
                                const liveCover = streamer.channels.find(
                                    (c) =>
                                        c.isLive &&
                                        (c.platform === 'twitch' || c.platform === 'youtube')
                                );
                                const liveMeta = liveCover || streamer.channels.find((c) => c.isLive);
                                return (
                                    <article key={streamer.username} className="live-card">
                                        <div className="live-card-top">
                                            <span className="live-badge">
                                                <span className="live-badge-dot" />
                                                {t('streamers.liveNow')}
                                            </span>
                                        </div>
                                        {liveCover ? (
                                            <LiveCover channel={liveCover} />
                                        ) : (
                                            <div className="live-avatar">
                                                <i className="ri-user-star-line" />
                                            </div>
                                        )}
                                        <div className="live-meta">
                                            <div className="live-avatar-mini">
                                                <i className="ri-user-star-line" />
                                            </div>
                                            <div className="live-meta-text">
<h3 className="live-name">
                                                    {streamer.displayName || streamer.username}
                                                </h3>
                                                <p className="live-game">
                                                    {liveMeta?.title || t('streamers.watching')}
                                                </p>
                                                {liveMeta?.game && (
                                                    <p className="live-game-tag">{liveMeta.game}</p>
                                                )}
                                                {(liveMeta?.viewerCount ?? 0) > 0 && (
                                                    <p className="live-viewers">
                                                        {t('streamers.viewers').replace(
                                                            '{count}',
                                                            (liveMeta?.viewerCount ?? 0).toLocaleString(
                                                                locale === 'pt-BR' ? 'pt-BR' : 'en-US'
                                                            )
                                                        )}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        {renderChannels(streamer.channels, true)}
                                    </article>
                                );
                            })}
                        </div>
                        {offline.length > 0 && (
                            <div className="lives-offline-section">
                                <h2 className="lives-offline-title">{t('streamers.offline')}</h2>
                                <div className="lives-grid">
                                    {offline.map((streamer) => (
                                        <article key={streamer.username} className="live-card live-card-offline">
                                            <div className="live-card-top">
                                                <span className="live-badge live-badge-offline">
                                                    {t('streamers.offline')}
                                                </span>
                                            </div>
                                            <div className="live-avatar">
                                                <i className="ri-user-star-line" />
                                            </div>
                                            <h3 className="live-name">{streamer.displayName || streamer.username}</h3>
                                        <p className="live-status">{t('streamers.watching')}</p>
                                            {renderChannels(streamer.channels, false)}
                                        </article>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="lives-empty">
                        <i className="ri-live-line lives-empty-icon" />
                        <h3>{t('streamers.noLive')}</h3>
                        <p>{t('streamers.noLiveDesc')}</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Streamers;